
import React, { useState } from 'react';
import { 
  startOfMonth,
  endOfMonth,
  getDay,
  getDate,
  isSameMonth,
  addDays,
  isToday,
  format
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { Video, Youtube } from 'lucide-react';
import { VideoEvent } from '@/hooks/useVideoEvents';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import VideoDetailsDialog from '../components/VideoDetailsDialog';

interface MonthViewProps {
  currentDate: Date;
  videoEvents: VideoEvent[];
  onVideoClick: (videoId?: string) => void;
}

const MonthView: React.FC<MonthViewProps> = ({ 
  currentDate, 
  videoEvents,
  onVideoClick
}) => {
  const [selectedVideo, setSelectedVideo] = useState<VideoEvent | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleVideoClick = (event: VideoEvent, e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setSelectedVideo(event);
    setIsDialogOpen(true);
    if (onVideoClick) onVideoClick(event.videoId);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedVideo(null);
  };

  const getMonthDays = () => {
    const firstDayOfMonth = startOfMonth(currentDate);
    const lastDayOfMonth = endOfMonth(currentDate);
    const startingDayOfWeek = getDay(firstDayOfMonth);
    
    const adjustedStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const prevMonthDays = Array.from({ length: adjustedStartDay }).map((_, i) => {
      return addDays(firstDayOfMonth, -(adjustedStartDay - i));
    });
    
    const daysInMonth = lastDayOfMonth.getDate();
    const currentMonthDays = Array.from({ length: daysInMonth }).map((_, i) => {
      return addDays(firstDayOfMonth, i);
    });
    
    const totalCells = Math.ceil((adjustedStartDay + daysInMonth) / 7) * 7;
    const nextMonthDays = Array.from({ length: totalCells - (adjustedStartDay + daysInMonth) }).map((_, i) => {
      return addDays(lastDayOfMonth, i + 1);
    });
    
    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  const getEventsForDate = (date: Date) => {
    return videoEvents.filter(event => 
      date.getDate() === event.date.getDate() && 
      date.getMonth() === event.date.getMonth() && 
      date.getFullYear() === event.date.getFullYear()
    );
  };

  const allDays = getMonthDays();
  const weeks = [];
  
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push(allDays.slice(i, i + 7));
  }

  const getEventStatusInfo = (event: VideoEvent) => {
    return {
      text: 'Publié',
      className: 'text-green-500'
    };
  };

  return (
    <>
      <div className="grid grid-cols-7 border-b dark:border-gray-800/50">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, index) => (
          <div 
            key={index} 
            className="text-center p-2 font-medium border-r last:border-r-0 dark:border-gray-800/50 dark:text-gray-300"
          >
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-auto-rows h-full">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7">
            {week.map((date, dayIndex) => {
              const dayNumber = getDate(date);
              const isCurrentMonth = isSameMonth(date, currentDate);
              const dayEvents = getEventsForDate(date);
              
              return (
                <div 
                  key={dayIndex} 
                  className={`border-r last:border-r-0 border-b p-2 min-h-[80px] dark:border-gray-800/50 ${
                    !isCurrentMonth ? 'bg-gray-50 text-gray-400 dark:bg-[#1F2233] dark:text-gray-500' : 'dark:bg-[#121624] dark:text-gray-300'
                  } ${
                    isToday(date) ? 'bg-blue-50 dark:bg-[#2D336B]/30' : ''
                  }`}
                >
                  <div className={`text-sm mb-2 ${
                    isToday(date) ? 'bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center dark:bg-custom-secondaryLight dark:text-custom-dark' : ''
                  }`}>
                    {dayNumber}
                  </div>
                  
                  {dayEvents.map(event => {
                    const statusInfo = getEventStatusInfo(event);
                    const eventDate = format(event.date, 'd MMMM yyyy', { locale: fr });
                    
                    return (
                      <HoverCard key={event.id} openDelay={100} closeDelay={100}>
                        <HoverCardTrigger asChild>
                          <div 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleVideoClick(event, e);
                            }}
                            className="bg-blue-100 text-blue-800 text-xs p-1 rounded mb-1 flex items-center cursor-pointer hover:bg-blue-200 dark:bg-[#2D336B]/40 dark:text-custom-secondaryLight dark:hover:bg-[#2D336B]/60"
                          >
                            <Video className="h-3 w-3 mr-1" />
                            {event.title.length > 15 ? `${event.title.substring(0, 15)}...` : event.title}
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80 p-0 border-none shadow-lg" side="right">
                          <div className="bg-white rounded-md shadow-lg overflow-hidden border border-gray-200 dark:bg-[#252A41] dark:border-gray-700/50">
                            <div className="flex items-center gap-2 p-3 border-l-4 border-green-500 bg-gray-50 dark:bg-[#2D3348] dark:text-gray-100">
                              <Youtube className="h-5 w-5 text-red-600" />
                              <div className="flex-1">
                                <h3 className="font-medium text-gray-900 dark:text-gray-100">{event.title}</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{eventDate} {event.time}</p>
                              </div>
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusInfo.className}`}>
                                {statusInfo.text}
                              </span>
                            </div>
                            
                            {event.videoId && (
                              <div className="relative">
                                <img 
                                  src={`https://img.youtube.com/vi/${event.videoId}/mqdefault.jpg`} 
                                  alt={event.title}
                                  className="w-full h-32 object-cover"
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://placehold.co/600x400?text=No+Thumbnail';
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-10 h-10 bg-black bg-opacity-70 rounded-full flex items-center justify-center">
                                    <div className="ml-1 w-0 h-0 border-y-[8px] border-l-[12px] border-y-transparent border-l-white"></div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <VideoDetailsDialog 
        video={selectedVideo}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
      />
    </>
  );
};

export default MonthView;
